// Login.tsx
import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Alert, Typography, Modal } from 'antd';
import { UserOutlined, LockOutlined, EyeOutlined, EyeInvisibleOutlined, MailOutlined } from '@ant-design/icons';
//import { useAuthStore } from '../../store/authStore'; // Import from the store we just created
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../../../context/UnifiedAuthContext';
import { atriumTokens } from '../../../../theme/theme';
import { apiService } from '../../services/api';
import { staticApi } from '../../utils/staticApi';

const { Title } = Typography;

export const LoginCondo: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, error, isLoading, resetError } = useAuth(); // useAuthStore();

  // Sprint R11-8 (ADR-128) — ProtectedRoute has always handed us the page the
  // visitor originally asked for (`state.from`), but we used to ignore it and
  // dump everyone on the role default. Now that pages have real URLs, a
  // bookmarked/shared deep link must survive the login bounce.
  //
  // Only honour an in-app condo path: `state` is router-controlled rather than
  // attacker-supplied, but validating the prefix keeps this from becoming an
  // open-redirect if the state ever gets sourced from a query param.
  //
  // Sprint 137 (ADR-132) — it now IS sourced from a query param, so that prefix
  // check went from prudent to load-bearing. `?next=` is hardLogout's fallback
  // channel for the expired-session path, where the full page load has already
  // wiped `state.from` (see httpClient's nextParamFor). Anyone can type
  // `?next=https://evil.example`; the check rejects it — an absolute URL, a
  // protocol-relative `//evil.example`, and any non-condo path all fail
  // startsWith and fall through to the role default.
  //
  // `state.from` stays PRIMARY: it is the router's own record of the request
  // and cannot be forged by a link, so it wins whenever both are present.
  const requestedPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const nextParam = new URLSearchParams(location.search).get('next');
  const condoPathOrNull = (p: string | null | undefined) =>
    p && p.startsWith('/condo-app/') ? p : null;
  const returnTo = condoPathOrNull(requestedPath) ?? condoPathOrNull(nextParam) ?? '/condo-app/';

  // Sprint 147 (ADR-142, R14-29) — own the document title.
  //
  // AtriumShell sets `<page> · CondoPro` on every in-app navigation but nothing
  // ever reset it, so a session that expired on, say, the Document Centre
  // bounced to this login screen still titled "Document Center · CondoPro" —
  // the tab claimed the user was somewhere they had been logged out of.
  // AtriumShell now clears it on unmount; this sets the honest title for the
  // full-page-load path (hardLogout), where no unmount happens at all.
  useEffect(() => {
    document.title = 'Sign in · CondoPro';
  }, []);

  // Sprint 124 (ADR-117) — forgot-password / activation request.
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSending, setForgotSending] = useState(false);

  const submitForgot = async () => {
    setForgotSending(true);
    try {
      await apiService.requestActivation(forgotEmail.trim());
    } catch {
      // Swallow — the message stays neutral either way (no enumeration).
    } finally {
      setForgotSending(false);
      setForgotOpen(false);
      setForgotEmail('');
      // Neutral, non-enumerating confirmation.
      staticApi.message.success('If that email has an account, a link was sent.');
    }
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    try {
      // login() returns true on success. We used to read `isAuthenticated`
      // from the render-captured closure here, which was always the PREVIOUS
      // render's value (i.e. false on first click) — so this branch never
      // fired and we relied on a hard window.location redirect in the
      // context instead. Using the return value lets us navigate via React
      // Router without tearing down the Zustand store (which caused the
      // "Verifying session..." spinner to hang after reload).
      const ok = await login(values.email, values.password, 'condo');
      if (ok) {
        // R11-8 — return to the deep link they asked for, else the role default
        // (the bare path redirects to it).
        navigate(returnTo, { replace: true });
      }
    } catch (err) {
      // Error is already handled by the store
      console.error('Login failed:', err);
    }
  };

  // Shared visible focus ring (no dedicated focus token in atriumTokens, so we
  // derive a clearly-visible outline from the brand blue). Applied via inline
  // focus/blur handlers to match this file's existing inline-style convention.
  const focusRing = `0 0 0 3px ${atriumTokens.blueS}, 0 0 0 1px ${atriumTokens.blue}`;
  const addFocusRing = (e: React.FocusEvent<HTMLElement>) => {
    e.currentTarget.style.outline = `2px solid ${atriumTokens.blue}`;
    e.currentTarget.style.outlineOffset = '2px';
    e.currentTarget.style.boxShadow = focusRing;
  };

  return (
    <main style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: atriumTokens.bg,
      fontFamily: atriumTokens.fontFamily,
      padding: '48px 16px'
    }}>
      <Card
        styles={{ body: { padding: 0 } }}
        style={{
          maxWidth: '448px',
          width: '100%',
          background: atriumTokens.card,
          boxShadow: atriumTokens.sh2,
          borderRadius: `${atriumTokens.radiusCard}px`,
          border: `1px solid ${atriumTokens.line}`,
          fontFamily: atriumTokens.fontFamily,
          overflow: 'hidden'
        }}
      >
        {/* Subtle Atrium-blue accent bar */}
        <div style={{
          height: '4px',
          background: atriumTokens.gradGreeting
        }} />
        <div style={{ padding: '28px 28px 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <Title level={1} style={{ color: atriumTokens.ink, margin: 0, fontSize: '28px', fontFamily: atriumTokens.fontFamily }}>
            Condo Portal Login
          </Title>
        </div>
        
        {error && (
          <Alert
            message="Login Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
            onClose={resetError}
            closable
          />
        )}
        
        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="email"
            label="Email Address"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
            style={{ marginBottom: '20px' }}
          >
            <Input
              prefix={<UserOutlined style={{ color: atriumTokens.mut }} />}
              placeholder="you@example.com"
              size="large"
              id="login-email"
              autoComplete="email"
              onFocus={addFocusRing}
              onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
              style={{
                borderRadius: `${atriumTokens.radiusControl}px`,
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true, message: 'Please input your password!' }]}
            style={{ marginBottom: '24px' }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: atriumTokens.mut }} />}
              placeholder="Enter your password"
              size="large"
              id="login-password"
              autoComplete="current-password"
              onFocus={addFocusRing}
              onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
              iconRender={(visible) => {
                const label = visible ? 'Hide password' : 'Show password';
                const Icon = visible ? EyeOutlined : EyeInvisibleOutlined;
                return (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={label}
                    title={label}
                    style={{ color: atriumTokens.mut, display: 'inline-flex', cursor: 'pointer' }}
                    onFocus={addFocusRing}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.outline = ''; }}
                  >
                    <Icon aria-hidden="true" />
                  </span>
                );
              }}
              style={{
                borderRadius: `${atriumTokens.radiusControl}px`,
                height: '48px',
                fontSize: '16px'
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              size="large"
              block
              style={{
                height: '48px',
                borderRadius: `${atriumTokens.radiusControl}px`,
                fontSize: '16px',
                fontWeight: 600,
                color: atriumTokens.card,
                background: atriumTokens.blue,
                border: 'none',
                boxShadow: atriumTokens.shc
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = atriumTokens.blueD; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = atriumTokens.blue; }}
              onFocus={(e) => {
                // Keyboard equivalent of the hover affordance + visible focus ring.
                e.currentTarget.style.background = atriumTokens.blueD;
                e.currentTarget.style.outline = `2px solid ${atriumTokens.blue}`;
                e.currentTarget.style.outlineOffset = '2px';
                e.currentTarget.style.boxShadow = focusRing;
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = atriumTokens.blue;
                e.currentTarget.style.outline = '';
                e.currentTarget.style.boxShadow = atriumTokens.shc;
              }}
            >
              Sign In
            </Button>
          </Form.Item>
        </Form>
        
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button
            type="link"
            onClick={() => { setForgotEmail(form.getFieldValue('email') || ''); setForgotOpen(true); }}
            style={{ padding: 0, fontSize: '14px' }}
          >
            Forgot password?
          </Button>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '16px',
          paddingTop: '16px',
          borderTop: `1px solid ${atriumTokens.line}`
        }}>
          <p style={{
            fontSize: '14px',
            color: atriumTokens.mut,
            margin: 0
          }}>
            Need help accessing your account? Contact management.
          </p>
        </div>
        </div>
      </Card>

      {/* Sprint 124 (ADR-117) — forgot-password / activation request. Posts to
          /api/auth/request-activation and always shows the same neutral message
          whether or not the email exists (no account enumeration). */}
      <Modal
        title="Reset your password"
        open={forgotOpen}
        onCancel={() => setForgotOpen(false)}
        onOk={submitForgot}
        okText="Send link"
        confirmLoading={forgotSending}
        okButtonProps={{ disabled: !forgotEmail.trim() }}
      >
        <p style={{ color: atriumTokens.mut, marginBottom: 12 }}>
          Enter your email and we'll send a link to set (or reset) your password.
          If your account was just created, this is how you activate it.
        </p>
        <Input
          type="email"
          prefix={<MailOutlined style={{ color: atriumTokens.mut }} />}
          placeholder="you@example.com"
          size="large"
          value={forgotEmail}
          onChange={(e) => setForgotEmail(e.target.value)}
          onPressEnter={() => { if (forgotEmail.trim()) submitForgot(); }}
        />
      </Modal>
    </main>
  );
};


export default LoginCondo;